import type { Exercise } from "../api/types";

/** Parcours du dossier d'exercice (demande d'Alexis du 12/10/2026) : fiche pays → mise en ambiance → OPO
 *  rédigé (brouillon) → OPO validé → dossier complet téléchargeable en un seul PDF. */

/** Cible de désignation de « Valider l'OPO » (vue de l'ordre). */
export const ORDER_APPROVE = "order-approve";
/** Cible de désignation de l'icône de téléchargement du conteneur « Dossier d'exercice » (barre latérale). */
export const DOSSIER_EXPORT = "dossier-export";

/** État de l'OPO affiché : pas encore rédigé, brouillon, validé. */
export type OrderState = "none" | "draft" | "approved";

/** OPO affiché du dossier : celui de la rotation en CENTAC, le seul ailleurs (comme la vue de l'ordre). */
export function orderNumber(exercise: Pick<Exercise, "parameters"> | undefined, opo: number): number {
  return exercise?.parameters.level === "CENTAC" ? opo : 1;
}

/** État de l'OPO `number` d'après la liste des dossiers ; une réponse sans numéros (ancienne) se lit aux
 *  décomptes, qui suffisent hors CENTAC. */
export function orderState(exercise: Pick<Exercise, "orders"> | undefined, number: number): OrderState {
  const orders = exercise?.orders;
  if (!orders || orders.count === 0) return "none";
  if (orders.numbers) {
    if (!orders.numbers.includes(number)) return "none";
    return orders.finalized_numbers?.includes(number) ? "approved" : "draft";
  }
  return orders.finalized > 0 && orders.finalized === orders.count ? "approved" : "draft";
}

export { STALE_REGENERATE } from "../components/attention";

/** Premier préalable manquant du dossier complet, dans l'ordre du dossier ; null s'il est prêt. Une fiche pays ou une
 *  mise en ambiance dont une rubrique ne suit plus le dossier (« À régénérer », « À revoir ») n'est pas assemblée avec
 *  l'OPO à jour (audit tactique du 15/10/2026, constat 2) : `regenerate_country`, `regenerate_briefing`. */
export type DossierMissing = "country" | "briefing" | "regenerate_country" | "regenerate_briefing" | "order" | "approve";

/** Refus du serveur (409 `DOSSIER_INCOMPLETE`) : `missing`, et pour une rubrique à reprendre `view`. */
export function missingFromServer(missing: unknown, view: unknown): DossierMissing | undefined {
  if (missing === "regenerate") {
    return view === "country" ? "regenerate_country" : view === "briefing" ? "regenerate_briefing" : undefined;
  }
  return missing === "country" || missing === "briefing" || missing === "order" || missing === "approve" ? missing : undefined;
}

/** Rubriques qui ne suivent plus le dossier : fiche pays (« À régénérer » ou « À revoir ») ; mise en ambiance
 *  (« À régénérer », hors paragraphe retouché à la main, comme le serveur). */
export function countryStale(sections: readonly { stale?: boolean }[] | undefined): boolean {
  return Boolean(sections?.some((s) => s.stale));
}

export function briefingStale(sections: readonly { stale?: boolean; edited?: boolean; text?: string }[] | undefined): boolean {
  return Boolean(sections?.some((s) => s.stale && !s.edited && s.text));
}

export function dossierMissing(
  country: boolean,
  briefing: boolean,
  order: OrderState,
  stale: { country?: boolean; briefing?: boolean } = {},
): DossierMissing | null {
  if (!country) return "country";
  if (!briefing) return "briefing";
  if (stale.country) return "regenerate_country";
  if (stale.briefing) return "regenerate_briefing";
  if (order === "none") return "order";
  if (order === "draft") return "approve";
  return null;
}
